import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { HiddenDirective, markAllTouched, ScreenSizeService } from '@nestech/pure-ui';
import { cassiopeiaIcons } from '@nestech/utils';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
  Button,
  Card,
  CardContainer,
  CardHeader,
  InputText,
  LanguagePickerComponent,
  Page,
  TopNavigationPhoneComponent,
  InterpolatedTranslatePipe,
} from '@web/common';
import { Authentication, EnvironmentService, LoginOption } from '@web/service';
import { from } from 'rxjs';

@Component({
  selector: 'cas-login',
  imports: [
    Button,
    TopNavigationPhoneComponent,
    Card,
    NgbDropdownModule,
    InterpolatedTranslatePipe,
    LanguagePickerComponent,
    Page,
    InputText,
    ReactiveFormsModule,
    CardContainer,
    CardHeader,
    HiddenDirective,
    FaIconComponent,
  ],
  template: `
    <header
      #header
      class="bg-cloud-white d-flex align-items-center">
      <nav class="ps-3 py-3">
        <button
          [uiHidden]="true"
          class="btn btn-cloud-white">
          <fa-icon
            [icon]="icon.bars"
            class="text-ink-light"
            [fixedWidth]="true" />
        </button>
      </nav>
      <a routerLink="/">
        <img
          class="ms-6 ms-md-10"
          [src]="logo()"
          alt="logo" />
      </a>
      <div class="col"></div>
      <cas-top-navigation-phone class="me-6" />
    </header>
    <main
      #main
      class="d-flex flex-column w-100 h-100 justify-content-between">
      <cas-page>
        @if (showLanguageSelector) {
          <cas-language-picker class="d-block mt-10" />
        }
        <cas-card-container xxlCols="1">
          <cas-card>
            <cas-card-header
              [name]="'cas.loginPageCardTitle' | translate: { companyName }"
              description="cas.loginPageCardSubtitle" />
            <div class="row gy-3 gy-md-0">
              @for (option of loginOptions(); track option.name) {
                <div class="col-12 col-md-auto">
                  <cas-button
                    [theme]="option.isDefault ? 'primary-dark' : 'primary-light'"
                    (click)="login(option)"
                    [block]="asBlock()"
                    [loading]="isLoading()">
                    {{ 'cas.authProvider.' + option.name | translate }}
                  </cas-button>
                </div>
              }
            </div>
            @if (isExternalIdLoginEnabled) {
              <form class="col-md-auto mt-3">
                <cas-input-text
                  id="externalId"
                  [formControl]="externalId"
                  (keydown.enter)="loginExternalId($event)"></cas-input-text>
              </form>
            }
            @if (optionAwaitingExternalId()) {
              <form class="col-md-auto mt-3">
                <cas-input-text
                  id="optionExternalId"
                  [formControl]="externalId"
                  (keydown.enter)="loginAwaitingOption($event)"></cas-input-text>
              </form>
            }
          </cas-card>
        </cas-card-container>
      </cas-page>
    </main>
  `,
  styles: `
    :host {
      height: 100vh;
    }
    header {
      img {
        height: 24px;
        @media (width > 576px) {
          height: 30px;
        }
      }
    }
  `,
})
export class LoginComponent {
  #authService = inject(Authentication);
  #environment = inject(EnvironmentService);
  #screenSize = inject(ScreenSizeService).screenSize;
  #isSmallScreen = computed(() => ['sm', 'xs'].includes(this.#screenSize() ?? ''));
  asBlock = this.#isSmallScreen;
  logo = computed(() => (this.#isSmallScreen() ? 'logo/logo.sm.svg' : 'logo/logo.svg'));

  readonly icon = cassiopeiaIcons;
  readonly companyName = this.#environment.environment.companyName;
  readonly showLanguageSelector = this.#environment.environment.availableLanguages.length > 1;
  readonly loginOptions = toSignal(from(this.#authService.loginOptions()), { initialValue: [] as LoginOption[] });

  /** Set once an option that needs a member has been picked; its input starts the login. */
  readonly optionAwaitingExternalId = signal<LoginOption | undefined>(undefined);

  isExternalIdLoginEnabled = this.#environment.environment.isExternalIdLoginEnabled;
  readonly isLoading = signal(false);
  readonly externalId = new FormControl('', [Validators.required]);

  login(option: LoginOption) {
    if (option.requiresExternalId) {
      this.optionAwaitingExternalId.set(option);
      return;
    }
    this.#startLogin(option);
  }

  loginAwaitingOption(event?: Event) {
    event?.preventDefault();
    const option = this.optionAwaitingExternalId();
    if (!option) {
      return;
    }
    if (this.externalId.invalid) {
      markAllTouched(this.externalId);
      return;
    }
    this.#startLogin(option, this.externalId.value ?? undefined);
  }

  #startLogin(option: LoginOption, externalId?: string) {
    this.isLoading.set(true);
    this.#authService.startLogin(option, externalId).catch(error => {
      this.isLoading.set(false);
      console.error(error);
    });
  }

  loginExternalId(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    if (this.externalId.invalid) {
      return;
    }
    const url = `${window.location.origin}/api/authorization/intra?externalId=${this.externalId.value}`;
    window.location.assign(url);
    return;
  }
}
